import { Component, Event, type EventEmitter, h, Host, Prop, Listen, Watch, State} from '@stencil/core';
import type { Facet, Filter } from '../../../api/SearchApi';
import {type FacetCose, FacetTypeEnum } from '../../../api/model/response/facets/Facets';
import type {FacetData} from '../../../models/common/facet-data.model';
import {FacetsService} from '../../../utils/services/facets/facets.service';
import { SearchableFacet } from './subcomponents/searchable-facet';
import { SearchableInputFilter } from './subcomponents/searchable-input-filter-model';
import type { SearchableFilterChanged } from './subcomponents/searchable-filter-changed';
import { makeLogger } from '../../../logger/logger';
import { cloneObjectWithNoReference } from '../../../utils/helpers/clone-object-with-no-reference';
import { FILTER_CATALOG_KEY } from '../../../constants/filterConstants';
import {FacetsV2Service} from '../../../utils/services/facets/facets-v2.service';
import type { labelProviderInterface } from '../../LabelProviderInterface';

const { info, warn } = makeLogger({
  pathToCurrentFile: 'components/result-page/facets/uiaas-search-facets.tsx',
  loggersList: ['info', 'debug', 'warn']
});

@Component({
  tag: 'uiaas-search-facets',
  shadow: false,
})
export class SearchFacets {
    @Prop({mutable: true}) facets: Array<Facet | FacetCose | FacetData>;
    @Prop() readonly parentFacet: Facet | FacetCose | FacetData;
    @Prop({mutable: true}) labelProvider?: labelProviderInterface;
    @Prop({mutable: true}) commonFilterTranslations?: labelProviderInterface;
    @Prop() readonly facetsLabel?: string;
    @Prop() readonly facetStateCose?: Object = {};
    @Prop() readonly openedFacets?: number;
    @Prop() readonly itemsLimit: number;
    @Prop({attribute: 'tab-id'}) readonly tabId: string;
    @Prop() readonly noHeaderFacets = [];
    @Prop() readonly mobileView: boolean;
    @Prop() readonly reflectedState;
    @Prop() readonly horizontalFilterPanel: boolean = false;
    @Prop() readonly searchFacetsValuesToRemove: Array<string>;
    @Prop() readonly disableFiltersOnLoading: boolean;
    @Prop() readonly enforcedFilters: Array<Filter> = [];
    @Prop() readonly facetsV2Service?: FacetsV2Service;
    /**
    * Creates input search term
    */
     @Prop() readonly searchableInputFilter: SearchableInputFilter;

    /**
    * Enables right-to-left view
     */
    @Prop() readonly rtl: boolean;

    @Prop() readonly scrollToTop: boolean = true;

    @Prop({mutable: false, attribute: 'hidden-facets'}) readonly hiddenFacets: string[] = [];

    @State() currentFacetOpened?: number;
    @State() searchFilterValue: string;
    @Prop() readonly productCatalog?: boolean;
    @Prop() readonly defaultQuartzColor?: string;

    @Listen('search:facetToggled')
    facetToggledListener(event: CustomEvent<number>) {
        this.handleToggleAllFacets(event.detail);
    }

    @State() productCatalogFilterSelected: boolean = false;
    @Listen('search:productCatalogChanged')
    productCatalogChanged(event: CustomEvent<boolean>) {
        this.productCatalogFilterSelected = !event.detail;
    }

    @Listen('search:reset')
    handleReset() {
        this.handleToggleAllFacets(-1);
    }

    private handleToggleAllFacets(index: number) {
        if (this.horizontalFilterPanel) {
          this.currentFacetOpened = index;
        }
    }

    private facetService!:FacetsService | FacetsV2Service;

    @Event({
      eventName: 'search:searchFacetButtonClicked',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      searchFacetButtonClicked: EventEmitter<any>; //eslint-disable-line @typescript-eslint/no-explicit-any

    @Event({
      eventName: 'search:searchInputFilterChanged',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      searchInputFilterChanged: EventEmitter<SearchableFilterChanged>;

    @Listen('click', {'target': 'window'})
    handleClick(ev) {
        if (ev === null) {
          return;
        }
        if (this.horizontalFilterPanel) {
          const isClickedOnFacet = ev.composedPath().map(i => i.tagName).includes('UIAAS-SEARCH-FACET');
          if (!isClickedOnFacet) {
            this.handleToggleAllFacets(-1);
          }
        }

        if(this.productCatalog){
          this.productCatalogFacetToggleVisible = false;
        }
    }

    @Listen('search:productCatalogWidgetClicked', { target: 'window' })
    handleProductCatalogWidgetClicked() {
        const widgetAlreadyClicked = window.sessionStorage.getItem('productCatalogWidgetClicked');
        if(widgetAlreadyClicked === null){
          this.productCatalogFacetToggleVisible = true;
          setTimeout(() => {
              this.productCatalogFacetToggleVisible = false;
          }, 2000);
        }
    }

    @State() productCatalogFacetToggleVisible = false;
    @Watch('facets')
    refactorFacets() {
        this.facets = FacetsService.setUnique(this.facets);
    }

    componentWillLoad() {
        this.facetService = FacetsService.getInstance(this.tabId);
        this.facets = FacetsService.setUnique(this.facets);
        if (!this.labelProvider) {
          info('Passing default LabelProvider for SearchFacets');
          this.labelProvider = (name, fallback) => {
              warn('labelProvider not defined for SearchFacets', [
                name,
                fallback,
              ]);
              return fallback;
          };
        }

        if (!this.commonFilterTranslations) {
          this.commonFilterTranslations = (name, fallback) => {
              warn(
                'commonFilterTranslations not defined for SearchFacets',
                [name, fallback]
              );
              return fallback;
          };
        }
        this.handleClick(null);
    }

    private static mapF(facets, parent = '', i = 0) {
        i++;
        Object.keys(facets).forEach((key) => {
            const inName = parent + ((i === 1) ? '' : ':') + facets[key][(i === 1) ? 'id' : 'value'];
            if (facets[key]['uniqueId'] !== inName) {
              if (facets[key]['uniqueId'] && i !== 1) {
                facets[key]['uniqueId'] += inName;
              } else {
                facets[key]['uniqueId'] = inName;
              }
            }

            if (facets[key]['children'].length > 0) {
              facets[key]['children'] = SearchFacets.mapF(facets[key]['children'], facets[key]['uniqueId'], i);
            }

            if (facets[key]['fieldValues'] && facets[key]['fieldValues'].length > 0) {
              facets[key]['fieldValues'] = SearchFacets.mapF(facets[key]['fieldValues'], facets[key]['uniqueId'], i);
            }
        });
        return facets;
    }


    private getDefaultOpenedValue(index) {
        return (this.openedFacets > -1 ? this.openedFacets > index : true);
    }

    private getProductCatalogFilterCount(source: string){
        const count = (this.facets.find(x => (x as FacetCose).id == 'catalog') as FacetCose)?.children.find(x => x.displayName == source)?.count;
        if(count === undefined){
          return 0;
        }

        return count;
    }

    private getCountryFilterSelected(){
        const countCountry = (this.facets.find(x => (x as FacetCose).id == 'catalog') as FacetCose)?.children.find(x => x.displayName == 'Country')?.count;
        const countGlobal = (this.facets.find(x => (x as FacetCose).id == 'catalog') as FacetCose)?.children.find(x => x.displayName == 'Global')?.count;
        if(countCountry === 0 && countGlobal > 0){
          return false;
        }
        return true;
    }

    private isOnlyHiddenFacetInState(): boolean {
        return this.facetService.facetsState$.value.filter(facet => !this.hiddenFacets.includes(facet.id)).length === 0;
    }

    render() {
        const nonSearchFacets = this.facets.filter(
          facet =>
            (facet as FacetCose).type !== FacetTypeEnum.Any
          && this.productCatalog ? (facet as FacetCose).id !== FILTER_CATALOG_KEY : true
        );
        return (
          <Host>
            <div class='facets-dir-container' dir={this.rtl ? 'rtl' : 'auto'}>
              <div>
                {(this.facetsLabel && this.horizontalFilterPanel) ? (
                  <div slot="start">
                    <div
                      class="search-facets-horizontal for-facets"
                    >
                      {this.renderSearchFacets()}
                    </div>
                    {this.facetsLabel}
                  </div>
                ) : ('')}
              </div>

              <div class={this.horizontalFilterPanel ? 'horizontal-panel' : 'empty'}>
                {this.facetsLabel ? (
                  <div class="facets__controls">
                    {(this.facetsLabel && !this.horizontalFilterPanel) ? (
                      <div
                        slot="start"
                        class="search-results-block-header for-facets"
                      >
                        {this.facetsLabel}
                      </div>
                    ) : (
                      ''
                    )}
                    {(!this.horizontalFilterPanel) && !this.isOnlyHiddenFacetInState() &&
                                ((Object.entries(this.facetStateCose).length
                                || this.facetService.facetsState$.value.length > 0) || (this.productCatalogFilterSelected)) ? (
                        <uiaas-search-reset-all-button
                          horizontalFilterPanel={this.horizontalFilterPanel}
                          disabledButton={this.disableFiltersOnLoading ? this.reflectedState.ajaxIsLoading : false}
                          labelProvider={(label, replacement) =>
                            this.labelProvider(
                              label,
                              replacement
                            )
                          }
                        ></uiaas-search-reset-all-button>
                      ) : (
                        (this.productCatalog && !this.productCatalogFilterSelected) ? (
                          <se-tooltip position="top" color="alternative">
                            <uiaas-search-reset-all-button
                              horizontalFilterPanel={this.horizontalFilterPanel}
                              disabledButton={true}
                              slot="trigger"
                              labelProvider={(label, replacement) =>
                                this.labelProvider(
                                  label,
                                  replacement
                                )
                              }
                            ></uiaas-search-reset-all-button>
                            <se-tooltip-content class="product-catalog-reset-all-tooltip-content">
                              {this.labelProvider('product.catalog.resetAll.tooltip.content','One Catalog filter option must be selected.')}
                            </se-tooltip-content>
                          </se-tooltip>
                        ) : ''
                      )}
                  </div>
                ) : (
                  ''
                )}
                {this.productCatalog ?
                  <span>
                    <span class="product-catalog-tooltip">
                      <span
                        class="product-catalog-tooltip-content"
                        id="product-catalog-tooltip-content"
                        style={
                          this.productCatalogFacetToggleVisible
                            ? {
                              opacity: '1',
                              visibility: 'visible',
                              zIndex: '999'
                            }
                            : null
                        }
                      >
                        {this.labelProvider(
                          'product.catalog.filter.tooltip.content',
                          'Country and global catalogs are available for viewing.'
                        )}
                      </span>
                    </span>
                  </span>
                  : ''}
                {
                  nonSearchFacets
                    ? this.renderFacet(cloneObjectWithNoReference<any>(nonSearchFacets)) //eslint-disable-line @typescript-eslint/no-explicit-any
                    : ''
                }
              </div>
            </div>
          </Host>
        );
    }

    private searchableInputFilterOnKeyPress(value, event?): void{
        if(event){
          if(event.key === 'Enter'){
            this.searchInputFilterChanged.emit({executeQuery: true, value: value});
            return;
          }
        }
        this.searchInputFilterChanged.emit({executeQuery: false, value: value});
    }

    private renderSearchFacets() {
        const searchFacets = this.facets.filter(facet => (facet as FacetCose)?.type == FacetTypeEnum.Any);
        return(
          <div>
            <div style={{'display' : 'inline-flex'}}>
              <div class={this.horizontalFilterPanel && (searchFacets.length > 0 || this.searchableInputFilter) ? 'horizontal-panel-search-facets' : 'empty' }>
                {this.searchableInputFilter ?
                  <div class='search-filter-input-container'>
                    <SearchableFacet
                      inputChanged={(value) => this.searchableInputFilterOnKeyPress(value)}
                      onKeyPress={(event, value) => this.searchableInputFilterOnKeyPress(value, event)}
                      inputLabel={this.labelProvider(this.searchableInputFilter.label, this.searchableInputFilter.label)}
                      placeholder={this.labelProvider('facet.placeholder.search', 'Search')}
                      inputValue={this.searchableInputFilter.value}
                    />
                  </div> : ''
                }
                {
                  searchFacets.length > 0
                    ? this.renderFacet(cloneObjectWithNoReference<any>(searchFacets)) //eslint-disable-line @typescript-eslint/no-explicit-any
                    : '' }
              </div>
              {this.horizontalFilterPanel && (searchFacets.length > 0 || this.searchableInputFilter) ?
                <se-button
                  option="outline"
                  color="secondary"
                  class="search-facets-button"
                  size="large"
                  style={{'display': 'table'}}
                  onClick={() => this.searchFacetButtonClicked.emit()}
                >{this.labelProvider('Search', 'Search')}</se-button>
                : ''}
            </div>
            {this.horizontalFilterPanel && (searchFacets.length > 0 || this.searchableInputFilter) ?
              <hr class="search-facets-divider"/>
              : ''}
          </div>
        );
    }

    private renderFacet(children: any[], isRoot = true) { //eslint-disable-line @typescript-eslint/no-explicit-any
        return (children.map((f, index) => (
          <span class={(f.fieldValues?.length > 0 && this.horizontalFilterPanel) ? 'facet-container' : ''} key={index + f.fieldName}>
            {(!this.hiddenFacets.includes(f['id']) && (f.selected || isRoot)) ?
              <span>
                <uiaas-search-facet
                  tab-id={this.tabId}
                  labelProvider={(label, replacement) =>
                    this.labelProvider(
                      f.fieldName + '.' + label,
                      replacement
                    )
                  }
                  facet-title={f.fieldName}
                  facetData={f}
                  facet-translation={f.fieldTranslation || f.displayName}
                  values={f.fieldValues || []}
                  facetStateCose={this.facetStateCose}
                  mobileView={this.mobileView}
                  hide-facet-title={
                    this.noHeaderFacets.indexOf(f.fieldName) >
                    -1
                  }
                  defaultOpened={this.getDefaultOpenedValue(
                    index
                  )}
                  items-limit={this.itemsLimit}
                  reflectedState={this.reflectedState}
                  disableFiltersOnLoading={
                    this.disableFiltersOnLoading
                  }
                  commonFilterTranslations={(e, f) =>
                    this.commonFilterTranslations(e, f)
                  }
                  rtl={this.rtl}
                  horizontalFilterPanel={this.horizontalFilterPanel}
                  index={index}
                  currentFacetOpened={this.currentFacetOpened}
                  searchFacetsValuesToRemove={this.searchFacetsValuesToRemove}
                  scrollToTop={this.scrollToTop}
                  defaultQuartzColor={this.defaultQuartzColor}
                />
                {
                  (f.selected && (f.children.length > 0))
                    ? (this.renderFacet(cloneObjectWithNoReference<any>(f.children), false)) //eslint-disable-line @typescript-eslint/no-explicit-any
                    : ''
                }
              </span> : ''}
          </span>)));
    }
}
